import { IonIcon, IonRow } from '@ionic/react';
import { LoadingFallback } from 'App';
import { pencil } from 'ionicons/icons';
import _ from 'lodash';
import { RunModel } from 'models/RunModel/RunModel';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRunEditContext } from '.';
import { extractRenderData } from '../../util';
import { ST } from '../../types';

import './css/FigureScorePopup.css';
import EditRunModal from '../../components/modals/EditRunModal';
import Icon from '../../components/icons/Icon';
import { RenderData, TimedData } from 'spinnaxBase/logic/types_storage';

type EditMode = 'trickname' | null;

type Indices = {
  [P in keyof RenderData]-?: number;
};

type CurrentRenderData = {
  [P in keyof RenderData]-?: string;
};

interface InterfaceCurrentRenderData extends CurrentRenderData {
  figureScore?: string;
}

const interpolate = (
  data: TimedData<string> | TimedData<number> | undefined,
  ms: number
): string => {
  if (!data) return '';

  const ret = { value: data.value, suffix: data.suffix };

  if (typeof ret.value === 'number') {
    const lastValue = data.lastValue as number;
    if (lastValue !== undefined) {
      const valueDiff = ret.value - lastValue;
      const timeOffset = ms - data.start;
      ret.value =
        lastValue + (timeOffset / (data.end - data.start)) * valueDiff;
    }

    if (ret.suffix === ' m' && ret.value < 1) {
      (ret.value as number) *= 100;
      ret.suffix = ' cm';
    }

    ret.value = ret.value.toFixed(1);
  } else {
    return ret.value || 'Unknown Figure';
  }
  if (ret.suffix) return `${ret.value} ${ret.suffix}`;
  return ret.value;
};

interface FigureScorePopupProps {
  record: RunModel;
  ms: number;
}

const FigureScorePopup: FunctionComponent<FigureScorePopupProps> = ({
  record,
  ms,
}) => {
  const [editMode, setEditMode] = useState<EditMode>(null);
  const playData = record?.playData;
  const [renderData, setRenderData] = useState<RenderData | null>(null);
  const indices = useRef<Indices | null>(null);

  const [currentData, setCurrentData] =
    useState<InterfaceCurrentRenderData | null>(null);

  const { onSectionRename, startEditMode } = useRunEditContext();

  // Aktuelle Run-Daten für das Modal vorbereiten
  const currentRunData = useMemo(() => {
    const currentFigureIndex = indices.current?.names ?? 0;
    const currentFigure: ST.PlayFigure | undefined =
      record?.playData?.figures?.[currentFigureIndex];

    const storedFigureScore = record.getFigureScore?.(currentFigureIndex);
    const figureScore =
      storedFigureScore?.computedScore ||
      currentFigure?.score ||
      record?.playData?.ranking?.figure_scores?.[currentFigureIndex]
        ?.computed_score ||
      0;

    const difficultyRating =
      storedFigureScore?.difficultyRating ||
      (currentFigure as any)?.difficulty_rating ||
      record?.playData?.ranking?.figure_scores?.[currentFigureIndex]
        ?.difficulty_rating ||
      0;

    const currentAngle = currentFigure?.angle
      ? `${currentFigure.angle}°`
      : '0°';

    const getRotationString = (figure: any): string => {
      const rotationCount = figure?.details?.rotation_count_uvw;
      if (rotationCount) {
        const maxRotation = Math.max(
          rotationCount.x || 0,
          rotationCount.y || 0,
          rotationCount.z || 0
        );

        if (maxRotation >= 4) return 'Quad';
        if (maxRotation >= 3) return 'Triple';
        if (maxRotation >= 2) return 'Double';
      }
      return '0';
    };

    const result = {
      name: currentData?.names || '',
      angle: currentAngle,
      hasBackside: currentFigure?.has_backside || false,
      stance: currentFigure?.stance || 'R',
      rotation: getRotationString(currentFigure),
      success: currentFigure?.achived || false,
      figureScore: figureScore,
      difficultyRating: difficultyRating,
    };

    return result;
  }, [
    currentData,
    record?.playData?.figures,
    record?.playData?.ranking?.figure_scores,
    record.figureScores,
    record,
  ]);

  // Handle save with business logic
  const handleModalSave = async (updatedData: {
    name: string;
    angle: string;
    hasBackside: boolean;
    stance: string;
    rotation: string;
    success: boolean;
  }) => {
    const angleStringToNumber = (angleString: string): number => {
      const numberStr = angleString.replace('°', '');
      return parseInt(numberStr) || 0;
    };

    onSectionRename(
      indices.current!.names,
      updatedData.name,
      angleStringToNumber(updatedData.angle),
      updatedData.hasBackside,
      updatedData.stance,
      updatedData.success,
      updatedData.rotation
    );

    if (renderData && indices.current) {
      renderData.names[indices.current.names].value = updatedData.name;
      setRenderData(_.clone(renderData));
    }

    setEditMode(null);
  };

  const editRunName = () => {
    setEditMode('trickname');
    startEditMode();
  };

  // refresh RenderData (data to display over the whole play)
  useEffect(() => {
    if (!playData) return;
    const data = extractRenderData(playData);
    setRenderData(data);
    indices.current = _.fromPairs<number>(
      (_.keys(data) as (keyof Indices)[]).map((key) => [key, 0])
    ) as Indices;
  }, [playData, setRenderData]);

  // get the data from `RenderData` to display at the moment
  const getCurrentData = useCallback(
    (_ms: number) => {
      const ret: Partial<InterfaceCurrentRenderData> = {};
      const _indices = indices.current;
      if (renderData && _indices !== null) {
        (_.keys(renderData) as (keyof RenderData)[]).forEach((key) => {
          let idx = _indices[key]!;
          let current: TimedData<any> | undefined = renderData[key]![idx];
          if (!current || current.start > _ms || current.end <= _ms) {
            current = undefined;
            const theData = renderData[key]!;
            idx = theData.findIndex(
              (value, cidx) =>
                value.start <= _ms &&
                (value.end >= _ms ||
                  (cidx === theData.length - 1 && value.end >= _ms))
            );
            if (idx !== -1) {
              current = renderData[key]![idx];
            }
          }
          _.set(_indices, key, idx);
          _.set(ret, key, interpolate(current, _ms));
        });

        const currentFigureIndex = _indices.names ?? 0;
        const figureScore =
          record?.playData?.ranking?.figure_scores?.[currentFigureIndex]
            ?.computed_score;

        if (figureScore !== undefined) {
          ret.figureScore = figureScore.toFixed(1);
        }
      }
      return ret as InterfaceCurrentRenderData;
    },
    [renderData, record?.playData?.ranking?.figure_scores]
  );

  useEffect(() => {
    setCurrentData(getCurrentData(ms));
  }, [ms, setCurrentData, getCurrentData]);

  if (!currentData) return <LoadingFallback />;

  return (
    <div>
      <EditRunModal
        isOpen={editMode === 'trickname'}
        onDidDismiss={() => setEditMode(null)}
        onCommit={handleModalSave}
        currentRunData={currentRunData}
      />

      <IonRow
        id="details-header"
        className="ion-align-items-center ion-justify-content-center"
      >
        {currentData.names && (
          <div
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '200px',
              overflow: 'visible',
            }}
          >
            {/* ✅ ICON + SCORE CONTAINER - relativ positioniert */}
            <div
              style={{
                position: 'relative', // ✅ Container für relatives Positioning
                display: 'inline-block', // ✅ Größe an Icon anpassen
              }}
            >
              <Icon name="Wow" size={100} />

              {/* ✅ SCORE - relativ zum Icon, mittig positioniert */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%', // ✅ Vertikal mittig
                  left: '50%', // ✅ Horizontal mittig
                  transform: 'translate(-50%, -50%)', // ✅ Perfekte Zentrierung
                  zIndex: 30, // ✅ Vor dem Icon (höher als Icon)
                  fontSize: '24px', // ✅ Passend zur Icon-Größe
                  fontWeight: 'bold',
                  color: '#ffffff',
                  textShadow:
                    '3px 3px 0px rgba(0,0,0,1), -1px -1px 0px rgba(0,0,0,0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  lineHeight: '1.1',
                  pointerEvents: 'none', // ✅ Klicks durchlassen
                }}
                className="floating-score-enhanced"
              >
                <span>{currentRunData.figureScore.toFixed(2)}</span>
                {currentRunData.difficultyRating > 0 && (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 'normal',
                      opacity: 0.9,
                      marginTop: '2px',
                      textShadow: '2px 2px 0px rgba(0,0,0,1)',
                    }}
                  >
                    D: {currentRunData.difficultyRating.toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            {/* ✅ FIGURE NAME - rechts neben dem Score/Icon */}
            <div
              style={{
                position: 'absolute',
                bottom: '60px',
                right: '20px',
                fontSize: '26px',
                fontWeight: 'bold',
                color: '#ffffff',
                textShadow: '3px 3px 6px rgba(0,0,0,0.9)',
                transform: 'rotate(-25deg)',
                transformOrigin: 'bottom left',
                zIndex: 15,
                maxWidth: '200px',
                textAlign: 'left',
              }}
              className="figure-name-rotated"
            >
              {currentData.names || 'Unknown Figure'}
            </div>

            {/* ✅ STATUS - unter dem Namen */}
            <div
              style={{
                position: 'absolute',
                bottom: '25px',
                right: '40px',
                fontSize: '18px',
                fontWeight: 'bold',
                color: currentRunData.success ? '#28a745' : '#dc3545',
                textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transform: 'rotate(-15deg)',
                transformOrigin: 'bottom left',
                zIndex: 15,
              }}
              className="status-rotated"
            >
              <span style={{ fontSize: '20px' }}>
                {currentRunData.success ? '✅' : '❌'}
              </span>
              <span>{currentRunData.success ? 'Achieved' : 'Failed'}</span>
            </div>

            {/* ✅ FIGURE DETAILS - links oben */}
            {(currentRunData.rotation !== '0' ||
              currentRunData.angle !== '0°') && (
              <div
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: currentRunData.success ? '#28a745' : '#ffc107',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  zIndex: 15,
                }}
                className="details-enhanced"
              >
                {currentRunData.rotation !== '0' && (
                  <span>{currentRunData.rotation}</span>
                )}
                {currentRunData.angle !== '0°' && (
                  <span>{currentRunData.angle}</span>
                )}
                <span
                  style={{
                    fontSize: '10px',
                    opacity: 0.8,
                  }}
                >
                  {currentRunData.stance}
                </span>
              </div>
            )}
          </div>
        )}
      </IonRow>
    </div>
  );
};

export default FigureScorePopup;
